import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'react-hot-toast';
import { Search, Plus, Eye, RefreshCw, FileText, CheckCircle2, XCircle, Send, CalendarX, Edit } from 'lucide-react';
import axios from 'axios';
import DataTable from '../components/common/DataTable';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getStatusBadge = (status) => {
  const badges = {
    'DRAFT': { color: 'bg-gray-100 text-gray-800', icon: FileText },
    'SENT': { color: 'bg-orange-100 text-orange-800', icon: Send },
    'ACCEPTED': { color: 'bg-green-100 text-green-800', icon: CheckCircle2 },
    'CONVERTED': { color: 'bg-emerald-100 text-emerald-800', icon: CheckCircle2 },
    'REJECTED': { color: 'bg-red-100 text-red-800', icon: XCircle },
    'EXPIRED': { color: 'bg-orange-100 text-orange-800', icon: CalendarX }
  };
  const badge = badges[status] || { color: 'bg-gray-100 text-gray-800', icon: FileText };
  const Icon = badge.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${badge.color}`}>
      <Icon className="w-3 h-3" /> {status}
    </span>
  );
};

const getValidityStatus = (validTo) => {
  const today = new Date();
  const validDate = new Date(validTo);
  const daysRemaining = Math.ceil((validDate - today) / (1000 * 60 * 60 * 24));
  if (daysRemaining < 0) return <span className="text-red-600 text-xs">Expired</span>;
  if (daysRemaining <= 7) return <span className="text-orange-600 text-xs">{daysRemaining}d left</span>;
  return <span className="text-green-600 text-xs">{daysRemaining}d left</span>;
};

const QuotationList = () => {
  const navigate = useNavigate();
  const { canWrite } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [quotations, setQuotations] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [salesOrgs, setSalesOrgs] = useState([]);
  const [filters, setFilters] = useState({ customer_id: '', status: '', sales_org: '', include_expired: false });

  useEffect(() => {
    loadMasterData();
    loadQuotations();
  // eslint-disable-next-line
  }, []);

  const loadMasterData = async () => {
    try {
      const [custRes, orgRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/customers`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/sales-distribution/sales-organizations`).catch(() => ({ data: [] }))
      ]);
      setCustomers(custRes.data || []);
      setSalesOrgs(orgRes.data || []);
    } catch (error) {
      console.error('Error loading master data:', error);
    }
  };

  const loadQuotations = async () => {
    setIsLoading(true);
    try {
      let url = `${BACKEND_URL}/api/presales/quotations`;
      const params = new URLSearchParams();
      if (filters.customer_id) params.append('customer_id', filters.customer_id);
      if (filters.status) params.append('status', filters.status);
      if (filters.sales_org) params.append('sales_org', filters.sales_org);
      if (filters.include_expired) params.append('include_expired', 'true');
      if (params.toString()) url += `?${params.toString()}`;
      const response = await axios.get(url);
      setQuotations(response.data || []);
    } catch (error) {
      console.error('Error loading quotations:', error);
      toast.error('Failed to load quotations');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = () => loadQuotations();

  const columns = [
    { key: 'quotation_number', header: 'Quotation #', filterable: true, accessor: (qt) => qt.quotation_number,
      render: (qt) => (
        <>
          <button onClick={() => navigate(`/presales/quotations/${qt.quotation_number}`)} className="font-mono font-medium text-orange-600 hover:underline">
            {qt.quotation_number}
          </button>
          {qt.reference_inquiry_number && (
            <p className="text-xs text-gray-500">
              From: <button onClick={() => navigate(`/presales/inquiries/${qt.reference_inquiry_number}`)} className="text-orange-500 hover:underline">{qt.reference_inquiry_number}</button>
            </p>
          )}
        </>
      ) },
    { key: 'version', header: 'Ver', align: 'center',
      render: (qt) => <span className="px-2 py-0.5 bg-gray-100 rounded text-xs font-mono">V{qt.version}</span> },
    { key: 'customer', header: 'Customer', filterable: true, accessor: (qt) => `${qt.customer_id} ${qt.customer_name}`,
      render: (qt) => (
        <div>
          <p className="font-medium text-gray-900">{qt.customer_id}</p>
          <p className="text-sm text-gray-500">{qt.customer_name}</p>
        </div>
      ) },
    { key: 'sales_organization', header: 'Sales Org', accessor: (qt) => qt.sales_organization },
    { key: 'valid_period', header: 'Valid Period',
      render: (qt) => (
        <div className="text-sm">
          <p>{qt.valid_from} - {qt.valid_to}</p>
          {getValidityStatus(qt.valid_to)}
        </div>
      ) },
    { key: 'net_value', header: 'Net Value', align: 'right',
      render: (qt) => <span className="font-mono">${qt.net_value.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span> },
    { key: 'gross_value', header: 'Gross Value', align: 'right',
      render: (qt) => <span className="font-mono font-semibold">${qt.gross_value.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span> },
    { key: 'status', header: 'Status', align: 'center', render: (qt) => getStatusBadge(qt.status) },
    { key: 'sales_orders', header: 'Sales Orders',
      render: (qt) => (qt.sales_order_numbers?.length > 0 ? (
        <div className="flex flex-wrap gap-1">
          {qt.sales_order_numbers.map(so => (
            <button key={so} onClick={() => navigate(`/otc/sales-orders/${so}`)} className="font-mono text-xs text-green-600 hover:underline">{so}</button>
          ))}
        </div>
      ) : <span className="text-gray-400">-</span>) },
    { key: 'actions', header: 'Actions', align: 'center',
      render: (qt) => (
        <div className="flex justify-center gap-1">
          <Button variant="ghost" size="sm" onClick={() => navigate(`/presales/quotations/${qt.quotation_number}`)} data-testid={`view-quotation-${qt.quotation_number}`}>
            <Eye className="w-4 h-4" />
          </Button>
          {canWrite('quotation') && (qt.status === 'DRAFT' || qt.status === 'SENT') && (
            <Button variant="ghost" size="sm" onClick={() => navigate(`/presales/quotations/${qt.quotation_number}/edit`)} data-testid={`edit-quotation-${qt.quotation_number}`}>
              <Edit className="w-4 h-4" />
            </Button>
          )}
        </div>
      ) },
  ];

  return (
    <Layout>
      <div className="p-6" data-testid="quotation-list">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Quotations</h1>
            <p className="text-sm text-gray-600">Manage customer quotations and price offers</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={loadQuotations} disabled={isLoading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            {canWrite('quotation') && (
            <Button onClick={() => navigate('/presales/quotations/create')} data-testid="create-quotation-btn">
              <Plus className="w-4 h-4 mr-2" />
              Create Quotation
            </Button>
            )}
          </div>
        </div>

        {/* Filters */}
        <Card className="p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Customer</label>
              <Select value={filters.customer_id || '__ALL__'} onValueChange={(v) => setFilters({...filters, customer_id: v === '__ALL__' ? '' : v})}>
                <SelectTrigger><SelectValue placeholder="All Customers" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__ALL__">All Customers</SelectItem>
                  {customers.map(c => (
                    <SelectItem key={c.customer_id} value={c.customer_id}>{c.customer_id} - {c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <Select value={filters.status || '__ALL__'} onValueChange={(v) => setFilters({...filters, status: v === '__ALL__' ? '' : v})}>
                <SelectTrigger><SelectValue placeholder="All Statuses" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__ALL__">All Statuses</SelectItem>
                  <SelectItem value="DRAFT">Draft</SelectItem>
                  <SelectItem value="SENT">Sent</SelectItem>
                  <SelectItem value="ACCEPTED">Accepted</SelectItem>
                  <SelectItem value="CONVERTED">Converted</SelectItem>
                  <SelectItem value="REJECTED">Rejected</SelectItem>
                  <SelectItem value="EXPIRED">Expired</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sales Org</label>
              <Select value={filters.sales_org || '__ALL__'} onValueChange={(v) => setFilters({...filters, sales_org: v === '__ALL__' ? '' : v})}>
                <SelectTrigger><SelectValue placeholder="All Sales Orgs" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__ALL__">All Sales Orgs</SelectItem>
                  {salesOrgs.map(org => (
                    <SelectItem key={org.sales_org_id} value={org.sales_org_id}>{org.sales_org_id} - {org.sales_org_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center pt-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={filters.include_expired} onChange={(e) => setFilters({...filters, include_expired: e.target.checked})} className="w-4 h-4 rounded" />
                <span className="text-sm">Include Expired</span>
              </label>
            </div>
            <div className="flex items-end">
              <Button onClick={handleSearch} className="w-full">
                <Search className="w-4 h-4 mr-2" />
                Search
              </Button>
            </div>
          </div>
        </Card>

        {/* Table */}
        <Card className="p-6">
          <DataTable
            testid="quotation-table"
            columns={columns}
            data={quotations}
            loading={isLoading}
            rowKey={(qt) => qt.quotation_number}
            emptyText="No quotations found"
            emptyHint="Create a quotation to get started."
          />
        </Card>
      </div>
    </Layout>
  );
};

export default QuotationList;
